import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const FinanceContext = createContext();

export const useFinance = () => useContext(FinanceContext);

const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api'
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const FinanceProvider = ({ children }) => {
  const [months, setMonths] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(true);

  const [reportsData, setReportsData] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(true);

  const fetchRecentMonths = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/months');
      setMonths(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const res = await api.get('/reports');
      setReportsData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const createMonth = async (data) => {
    const res = await api.post('/months', data);
    return res.data;
  };

  const fetchMonthDetails = useCallback(async (id) => {
    setMonthLoading(true);
    try {
      const res = await api.get(`/months/${id}`);
      setCurrentMonth(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setMonthLoading(false);
    }
  }, []);
  
  const addBudget = async (data) => {
    await api.post('/budgets', data);
    if(currentMonth) fetchMonthDetails(currentMonth.month.id);
  };
  
  const addMultipleBudgets = async (items) => {
    await Promise.all(items.map(data => api.post('/budgets', data)));
    if(currentMonth) fetchMonthDetails(currentMonth.month.id);
  };
  
  const addDebt = async (data) => {
    await api.post('/debts', data);
    if(currentMonth) fetchMonthDetails(currentMonth.month.id);
  };

  const addMultipleDebts = async (items) => {
    await Promise.all(items.map(data => api.post('/debts', data)));
    if(currentMonth) fetchMonthDetails(currentMonth.month.id);
  };

  const updateMonth = useCallback(async (id, data) => {
    await api.put(`/months/${id}`, data);
    fetchRecentMonths();
    if(currentMonth && currentMonth.month.id === id) {
      fetchMonthDetails(id);
    }
  }, [fetchRecentMonths, currentMonth, fetchMonthDetails]);

  const deleteMonth = useCallback(async (id) => {
    await api.delete(`/months/${id}`);
    fetchRecentMonths();
  }, [fetchRecentMonths]);

  const updateBudget = async (id, data) => {
    await api.put(`/budgets/${id}`, data);
    if(currentMonth) fetchMonthDetails(currentMonth.month.id);
  };

  const deleteBudget = async (id) => {
    await api.delete(`/budgets/${id}`);
    if(currentMonth) fetchMonthDetails(currentMonth.month.id);
  };

  const updateDebt = async (id, data) => {
    await api.put(`/debts/${id}`, data);
    if(currentMonth) fetchMonthDetails(currentMonth.month.id);
  };

  const deleteDebt = async (id) => {
    await api.delete(`/debts/${id}`);
    if(currentMonth) fetchMonthDetails(currentMonth.month.id);
  };

  const value = {
    months,
    currentMonth,
    fetchRecentMonths,
    createMonth,
    fetchMonthDetails,
    updateMonth,
    deleteMonth,
    addBudget,
    addMultipleBudgets,
    updateBudget,
    deleteBudget,
    addDebt,
    addMultipleDebts,
    updateDebt,
    deleteDebt,
    loading,
    monthLoading,
    reportsData,
    reportsLoading,
    fetchReports
  };

  return (
    <FinanceContext.Provider value={value}>
      {children}
    </FinanceContext.Provider>
  );
};
